/**
 * InvoicesPanel (Phase D1.1-C · Feb 2026)
 * ---------------------------------------
 * Commercial Document Center — Invoices section.
 *
 * Invoices are created ONLY by converting an approved Estimate.
 * They are IMMUTABLE snapshots — no manual edits, no line-item changes.
 * Payments + Credit Notes flow through the detail modal.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import InvoiceDetailModal from "@/components/InvoiceDetailModal";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const STATUS_TONE = {
  unpaid: "bg-amber-100 text-amber-900 border-amber-300",
  partial_paid: "bg-cyan-100 text-cyan-900 border-cyan-300",
  paid: "bg-emerald-100 text-emerald-900 border-emerald-300",
  due_on_delivery: "bg-purple-100 text-purple-900 border-purple-300",
  cancelled: "bg-red-100 text-red-900 border-red-300",
  credited: "bg-neutral-200 text-neutral-800 border-neutral-300",
};
const STATUS_LABEL_KEYS = {
  unpaid: "inv_status_unpaid",
  partial_paid: "inv_status_partial_paid",
  paid: "inv_status_paid",
  due_on_delivery: "inv_status_due_on_delivery",
  cancelled: "inv_status_cancelled",
  credited: "inv_status_credited",
};

export default function InvoicesPanel({ initialPendingCost = false, onPendingCostConsumed }) {
  const { t } = useI18n();
  const { isAdmin, can } = useAuth();
  const canViewAll = isAdmin || can("commercial", "view_all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [pendingCost, setPendingCost] = useState(initialPendingCost);
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (initialPendingCost) onPendingCostConsumed && onPendingCostConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (pendingCost) params.pending_cost = true;
      if (q.trim()) params.q = q.trim();
      const { data } = await axios.get(`${API}/commercial/invoices`, {
        params,
        withCredentials: true,
      });
      setItems(data);
    } catch (e) {
      toast.error(t("inv_panel_load_err"));
    } finally {
      setLoading(false);
    }
  }, [status, q, pendingCost]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/commercial/settings`, {
          withCredentials: true,
        });
        setSettings(data);
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const downloadPdf = async (inv) => {
    // Feb 2026 — ventana síncrona en el clic (anti popup-blocker).
    const w = window.open("", "_blank");
    try {
      const res = await axios.get(`${API}/commercial/invoices/${inv.id}/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(res.data);
      if (w) w.location = url;
      else window.open(url, "_blank");
    } catch {
      if (w) w.close();
      toast.error(t("inv_panel_pdf_err"));
    }
  };

  return (
    <div data-testid="invoices-panel" className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-overline">DOC / INV</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
            {t("inv_panel_title")}
          </h1>
          <p className="text-base text-[var(--text-secondary)] mt-1">
            {canViewAll ? t("inv_panel_subtitle_all") : t("inv_panel_subtitle_mine")}
            {" · "}
            {t("results_count", { n: items.length, s: items.length === 1 ? "" : "s" })}
          </p>
        </div>
        <div className="font-mono-tech text-xs text-[var(--text-secondary)] max-w-md text-right">
          {t("inv_panel_note_a")}
          <br />
          {t("inv_panel_note_b")}
        </div>
      </header>

      {/* Filters */}
      <section className="bg-white border border-[var(--border)] p-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 overflow-x-auto">
          {["all", "unpaid", "partial_paid", "paid", "due_on_delivery", "cancelled", "credited"].map(
            (k) => (
              <button
                key={k}
                data-testid={`invoices-filter-${k}`}
                onClick={() => setStatus(k)}
                className={`font-mono-tech text-xs px-3 py-1.5 border transition-colors whitespace-nowrap ${
                  status === k
                    ? "bg-black text-white border-black"
                    : "border-[var(--border)] hover:bg-black hover:text-white"
                }`}
              >
                {k === "all" ? t("filter_all_f") : t(STATUS_LABEL_KEYS[k])}
              </button>
            )
          )}
          {isAdmin && (
            <button
              data-testid="invoices-filter-pending-cost"
              onClick={() => setPendingCost((v) => !v)}
              className={`font-mono-tech text-xs px-3 py-1.5 border transition-colors whitespace-nowrap ${
                pendingCost
                  ? "bg-amber-500 text-black border-amber-600 font-bold"
                  : "border-amber-400 text-amber-800 hover:bg-amber-100"
              }`}
            >
              ⚠ {t("inv_filter_pending_cost")}
            </button>
          )}
        </div>
        <input
          data-testid="invoices-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("inv_panel_search_placeholder")}
          className="flex-1 min-w-[240px] border border-[var(--border)] px-3 py-2 text-sm"
        />
      </section>

      {/* Table */}
      <section className="bg-white border border-[var(--border)] overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-[var(--bg)]">
            <tr>
              <Th>{t("inv_panel_col_invoice")}</Th>
              <Th>{t("inv_panel_col_origin")}</Th>
              <Th>{t("inv_panel_col_client")}</Th>
              <Th>{t("inv_panel_col_status")}</Th>
              <Th className="text-right">{t("inv_panel_col_total")}</Th>
              <Th className="text-right">{t("inv_panel_col_paid")}</Th>
              <Th className="text-right">{t("inv_panel_col_balance")}</Th>
              <Th>{t("inv_panel_col_created")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {loading && (
              <tr>
                <td colSpan={9} className="p-6 text-center text-sm text-[var(--text-secondary)]">
                  {t("loading_ellipsis")}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="p-6 text-center text-sm text-[var(--text-secondary)]">
                  {t("inv_panel_empty")}
                </td>
              </tr>
            )}
            {!loading &&
              items.map((inv) => (
                <tr
                  key={inv.id}
                  data-testid={`invoices-row-${inv.number}`}
                  className="hover:bg-[var(--bg)] transition-colors"
                >
                  <Td>
                    <button
                      onClick={() => setDetailId(inv.id)}
                      className="font-mono-tech text-sm font-bold underline decoration-dotted"
                      data-testid={`invoices-open-${inv.number}`}
                    >
                      {inv.number}
                    </button>
                  </Td>
                  <Td>
                    <div className="text-xs text-[var(--text-secondary)]">
                      {inv.origin_estimate_number}
                    </div>
                  </Td>
                  <Td>
                    <div className="text-sm font-bold">{inv.company}</div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      {inv.contact_person}
                    </div>
                  </Td>
                  <Td>
                    <span
                      className={`font-mono-tech text-[0.7rem] font-bold tracking-wider px-2 py-1 border ${
                        STATUS_TONE[inv.status] || "bg-neutral-100"
                      }`}
                    >
                      {STATUS_LABEL_KEYS[inv.status] ? t(STATUS_LABEL_KEYS[inv.status]) : inv.status}
                    </span>
                  </Td>
                  <Td className="text-right font-mono-tech text-sm">
                    {money(inv.total_amount)}
                  </Td>
                  <Td className="text-right font-mono-tech text-sm text-emerald-700">
                    {money(inv.payments_total)}
                  </Td>
                  <Td
                    className={`text-right font-mono-tech text-sm font-bold ${
                      inv.balance_due > 0 ? "text-[var(--accent-magenta,#E5007D)]" : "text-emerald-700"
                    }`}
                  >
                    {money(inv.balance_due)}
                  </Td>
                  <Td>
                    <div className="text-xs">{String(inv.created_at || "").slice(0, 10)}</div>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => downloadPdf(inv)}
                        data-testid={`invoices-pdf-${inv.number}`}
                        className="font-mono-tech text-[0.65rem] font-bold border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors"
                      >
                        PDF
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
          </tbody>
        </table>
      </section>

      {detailId && (
        <InvoiceDetailModal
          invoiceId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
          settings={settings}
        />
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-3 py-2 text-left font-mono-tech text-[0.7rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 ${className}`}>{children}</td>;
}
